// src/lib/biometricSignIn.ts — Face ID / Touch ID sign-in (native app only).
//
// How it works (server half: routes/auth.js "device keys", lib/deviceKeys.js):
//   • Enrolling asks the server for a per-install DEVICE KEY (random secret) and
//     stores it in the phone's keychain. The server keeps only its hash.
//   • "Sign in with Face ID" = Face ID prompt → read the key → POST
//     /auth/device-login → the server opens a normal session and hands back a
//     ROTATED key, which replaces the stored one before we navigate.
//   • Sign-out keeps the key (that's the point: "Welcome Back!" → one glance).
//     A password reset revokes every key server-side; the phone re-enrolls
//     itself on the next normal sign-in because the opt-in survives locally.
//
// Works for every account type — Google, Apple and email — because the key is
// ours, not the password. This REPLACES the old scheme that stored the raw
// email + password in the keychain (server "requesta.app", flag
// rq_biometric_email); those users keep working through the legacy branch of
// loginWithBiometrics and are silently migrated onto a device key.
//
// Static plugin import + isNativePlatform() guard: dynamic import() chunks never
// load in the Capacitor WKWebView (see nativePush.ts). Never `await` a plugin
// object itself — only its methods.

import { Capacitor } from "@capacitor/core";
import { NativeBiometric, BiometryType, BiometricAuthError } from "capacitor-native-biometric";
import api from "../api";
import type { User } from "../types/User";

export const IS_NATIVE = Capacitor.isNativePlatform();

// ── storage keys ────────────────────────────────────────────────────────────
const DEVICE_ID_KEY = "rq_device_id";
// Which account this install's key belongs to. Presence == "armed" (a key is
// in the keychain), so the login page can offer the button without touching
// the keychain before Face ID has been passed.
const ARMED_KEY = "rq_bio_armed";
// Per-account choices, so two talents sharing a phone each get asked once.
const OPTIN_PREFIX = "rq_bio_optin:";
const PROMPTED_PREFIX = "rq_bio_prompted:";
// Pre-device-key scheme (raw email + password in the keychain).
const LEGACY_EMAIL_KEY = "rq_biometric_email";
const LEGACY_KEYCHAIN_SERVER = "requesta.app";
const KEYCHAIN_SERVER = "requesta.app/device";

export interface ArmedInfo {
  userId: string;
  name?: string;
}

// Same shape LoginPage's routeAfterLogin consumes.
export interface DeviceLoginResponse {
  accessToken?: string;
  user?: User;
  needsStripeFinish?: boolean;
  stripeRejected?: boolean;
  stripeRejectionCode?: string | null;
  deviceToken?: string;
}

export type BiometricErrorCode = "unavailable" | "cancelled" | "expired" | "network" | "failed";

export class BiometricError extends Error {
  code: BiometricErrorCode;
  constructor(code: BiometricErrorCode, message?: string) {
    super(message || code);
    this.code = code;
  }
}

// ── small helpers ───────────────────────────────────────────────────────────
function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}
function lsDel(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
      return (crypto as any).randomUUID().replace(/-/g, "");
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`;
  }
}

/** Stable per-install id (matches the server's DEVICE_ID_RE: 8–64 [A-Za-z0-9_-]). */
export function getDeviceId(): string {
  let id = lsGet(DEVICE_ID_KEY);
  if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
    id = randomId();
    lsSet(DEVICE_ID_KEY, id);
  }
  return id;
}

function platformLabel(): { platform: string; label: string } {
  const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const label = /iPad/i.test(ua) ? "iPad" : platform === "ios" ? "iPhone" : platform === "android" ? "Android" : "Device";
  return { platform, label };
}

// Plugin errors carry `code` (BiometricAuthError) on native; treat the user
// backing out as "cancelled", everything else as "failed".
function isCancelError(err: any): boolean {
  const code = err && (err.code ?? err.errorCode);
  const n = typeof code === "string" ? parseInt(code, 10) : code;
  // 11 APP_CANCEL, 15 SYSTEM_CANCEL, 16 USER_CANCEL, 17 USER_FALLBACK (no passcode set)
  if (n === 11 || n === 15 || n === 16 || n === 17) return true;
  const msg = String((err && err.message) || "").toLowerCase();
  return msg.includes("cancel");
}

// ── availability + wording ─────────────────────────────────────────────────
export interface BiometricAvailability {
  available: boolean;
  label: string;
  /**
   * iOS has Face ID switched off for Request App: "Don't Allow" on Apple's
   * one-time question, or the app's switch in iPhone Settings. Apple never
   * asks twice, so only iPhone Settings can undo it.
   */
  blocked: boolean;
}

let availabilityCache: Promise<BiometricAvailability> | null = null;

/**
 * "Face ID" / "Touch ID" (iOS) or a generic word elsewhere. Cached per launch;
 * `fresh` asks the phone again (Settings re-reads on every open and on the
 * way back from iPhone Settings) and refreshes the cache for everyone.
 */
export function getBiometricAvailability(opts: { fresh?: boolean } = {}): Promise<BiometricAvailability> {
  if (!IS_NATIVE) return Promise.resolve({ available: false, label: "Face ID", blocked: false });
  if (!availabilityCache || opts.fresh) {
    availabilityCache = NativeBiometric.isAvailable({ useFallback: false })
      .then((r) => {
        const t = r && r.biometryType;
        const label =
          t === BiometryType.TOUCH_ID
            ? "Touch ID"
            : t === BiometryType.FACE_ID
            ? "Face ID"
            : t === BiometryType.FINGERPRINT
            ? "fingerprint"
            : t === BiometryType.FACE_AUTHENTICATION
            ? "face unlock"
            : "Face ID";
        const available = !!(r && r.isAvailable);
        // A blocked app gets LAError.biometryNotAvailable, which the plugin
        // reports as BIOMETRICS_UNAVAILABLE (and biometryType 0, so the label
        // stays "Face ID" — the only kind iOS lets a user block per app).
        // "No hardware" says the same, but every iPhone this app installs on
        // (iOS 15+) has Face ID or Touch ID. Android has no per-app switch.
        const blocked =
          !available &&
          Capacitor.getPlatform() === "ios" &&
          Number(r && r.errorCode) === BiometricAuthError.BIOMETRICS_UNAVAILABLE;
        return { available, label, blocked };
      })
      .catch(() => ({ available: false, label: "Face ID", blocked: false }));
  }
  return availabilityCache;
}

// ── local state ─────────────────────────────────────────────────────────────
export function getArmed(): ArmedInfo | null {
  const raw = lsGet(ARMED_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v.userId === "string" ? v : null;
  } catch {
    return null;
  }
}
function setArmed(info: ArmedInfo | null) {
  if (info) lsSet(ARMED_KEY, JSON.stringify(info));
  else lsDel(ARMED_KEY);
}
export function hasLegacyCredentials(): boolean {
  return !!lsGet(LEGACY_EMAIL_KEY);
}
export function hasOptedIn(userId: string): boolean {
  return lsGet(OPTIN_PREFIX + userId) === "1";
}
export function setOptIn(userId: string, on: boolean) {
  if (on) lsSet(OPTIN_PREFIX + userId, "1");
  else lsDel(OPTIN_PREFIX + userId);
}
export function wasPrompted(userId: string): boolean {
  return lsGet(PROMPTED_PREFIX + userId) === "1";
}
export function markPrompted(userId: string) {
  lsSet(PROMPTED_PREFIX + userId, "1");
}
/** True when THIS account's key is the one in the keychain. */
export function isArmedFor(userId: string): boolean {
  const a = getArmed();
  return !!a && a.userId === userId;
}

/** Login page: can we show the "Sign in with Face ID" button on this install? */
export async function canOfferBiometricLogin(): Promise<boolean> {
  if (!IS_NATIVE) return false;
  if (!getArmed() && !hasLegacyCredentials()) return false;
  const { available } = await getBiometricAvailability();
  return available;
}

// ── auto-prompt (banking-app behaviour) ────────────────────────────────────
// Landing on the login page NOT signed in (session lapsed, cold start after a
// password reset, etc.) fires Face ID straight away — no tap. The one landing
// that must NOT auto-fire is the one produced by an explicit "Log out" tap
// (handing the phone to a friend, switching accounts): the button stays, the
// sheet doesn't jump in their face. sessionStorage lives exactly as long as
// the app process, so the suppression clears itself on the next cold launch.
const SKIP_AUTO_KEY = "rq_bio_skip_auto";
export function markExplicitSignOut() {
  try {
    sessionStorage.setItem(SKIP_AUTO_KEY, "1");
  } catch {}
}
export function shouldAutoPromptBiometric(): boolean {
  if (!IS_NATIVE) return false;
  try {
    return sessionStorage.getItem(SKIP_AUTO_KEY) !== "1";
  } catch {
    return true;
  }
}

// ── keychain ────────────────────────────────────────────────────────────────
async function storeKey(userId: string, deviceId: string, token: string) {
  await NativeBiometric.setCredentials({
    server: KEYCHAIN_SERVER,
    username: `${userId}:${deviceId}`,
    password: token,
  });
}
async function readKey(): Promise<{ userId: string; deviceId: string; token: string } | null> {
  try {
    const c = await NativeBiometric.getCredentials({ server: KEYCHAIN_SERVER });
    if (!c || !c.username || !c.password) return null;
    const i = c.username.indexOf(":");
    if (i <= 0) return null;
    return { userId: c.username.slice(0, i), deviceId: c.username.slice(i + 1), token: c.password };
  } catch {
    return null;
  }
}
async function clearKey() {
  try {
    await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER });
  } catch {}
  setArmed(null);
}
async function clearLegacy() {
  try {
    await NativeBiometric.deleteCredentials({ server: LEGACY_KEYCHAIN_SERVER });
  } catch {}
  lsDel(LEGACY_EMAIL_KEY);
}

async function verifyIdentity(purposeSubtitle: string) {
  const { label } = await getBiometricAvailability();
  try {
    await NativeBiometric.verifyIdentity({
      title: "Request App",
      subtitle: purposeSubtitle.replace("{label}", label),
      reason: purposeSubtitle.replace("{label}", label),
      useFallback: true,
    });
  } catch (err) {
    throw new BiometricError(isCancelError(err) ? "cancelled" : "failed");
  }
}

// ── enroll / re-arm / disable ───────────────────────────────────────────────
/**
 * Turn Face ID sign-in on for the signed-in talent on this install.
 * Requires a live session (the api instance carries the access token).
 * verifyFirst=true makes the phone owner prove presence before we mint a key
 * (prompt / Preferences switch); false = silent re-arm after a normal login.
 */
export async function enrollBiometricSignIn(
  user: Pick<User, "_id" | "name">,
  opts: { verifyFirst?: boolean } = {}
): Promise<void> {
  if (!IS_NATIVE) throw new BiometricError("unavailable");
  const { available } = await getBiometricAvailability();
  if (!available) throw new BiometricError("unavailable");
  if (opts.verifyFirst !== false) {
    await verifyIdentity("Use {label} to sign in next time");
    // "Don't Allow" on Apple's Face ID question can still pass this check
    // (iOS falls back to the passcode), but Face ID is now blocked for the
    // app and a key minted now could never be used — ask the phone again.
    if (!(await getBiometricAvailability({ fresh: true })).available) throw new BiometricError("unavailable");
  }

  const deviceId = getDeviceId();
  const { platform, label } = platformLabel();
  let token: string;
  try {
    const { data } = await api.post<{ deviceToken: string }>("/auth/device-token", {
      client: "app",
      deviceId,
      label,
      platform,
    });
    token = data && data.deviceToken;
    if (!token) throw new Error("no token");
  } catch (err: any) {
    throw new BiometricError(err && err.response ? "failed" : "network");
  }
  try {
    await storeKey(user._id, deviceId, token);
  } catch {
    throw new BiometricError("failed");
  }
  setArmed({ userId: user._id, name: user.name });
  setOptIn(user._id, true);
  // Old email+password scheme is superseded on this install.
  if (hasLegacyCredentials()) await clearLegacy();
}

/**
 * After ANY successful sign-in: if this account opted in but the install
 * isn't armed for it (first time, key expired/revoked, another account was
 * armed), quietly re-issue. Never throws, never blocks the login.
 */
export async function rearmBiometricSignInIfNeeded(user: Pick<User, "_id" | "name"> | null | undefined): Promise<void> {
  if (!IS_NATIVE || !user || !user._id) return;
  if (!hasOptedIn(user._id) || isArmedFor(user._id)) return;
  try {
    await enrollBiometricSignIn(user, { verifyFirst: false });
  } catch {
    /* next login tries again */
  }
}

/** Preferences switch → off. Revokes server-side and forgets the key. */
export async function disableBiometricSignIn(userId: string): Promise<void> {
  if (!IS_NATIVE) return;
  try {
    await api.delete("/auth/device-token", { data: { deviceId: getDeviceId() } });
  } catch {
    /* key is being forgotten locally regardless; server row expires on its own */
  }
  await clearKey();
  setOptIn(userId, false);
}

// ── sign in ─────────────────────────────────────────────────────────────────
/**
 * The "Sign in with Face ID" button. Resolves with the same payload as
 * /auth/login so the caller can hand it straight to routeAfterLogin.
 * Throws BiometricError: cancelled (user backed out — say nothing),
 * expired (key no longer valid — button hides, normal doors remain),
 * network / failed / unavailable.
 */
export async function loginWithBiometrics(): Promise<DeviceLoginResponse> {
  if (!IS_NATIVE) throw new BiometricError("unavailable");
  const armed = getArmed();
  const legacy = !armed && hasLegacyCredentials();
  if (!armed && !legacy) throw new BiometricError("expired");

  await verifyIdentity("Use {label} to sign in");

  if (armed) {
    const key = await readKey();
    if (!key) {
      setArmed(null);
      throw new BiometricError("expired");
    }
    try {
      const { data } = await api.post<DeviceLoginResponse>("/auth/device-login", {
        deviceToken: key.token,
        deviceId: key.deviceId,
      });
      // Store the rotated key BEFORE anything else — the old one is dead now.
      if (data && data.deviceToken) {
        try {
          await storeKey(key.userId, key.deviceId, data.deviceToken);
        } catch {}
      }
      if (data && data.user) setOptIn(data.user._id, true);
      return data;
    } catch (err: any) {
      const status = err && err.response && err.response.status;
      if (status === 401) {
        // Revoked / expired / rotated-away: forget the key but KEEP the opt-in so
        // the next normal sign-in silently re-arms this install.
        await clearKey();
        throw new BiometricError("expired");
      }
      throw new BiometricError(err && err.response ? "failed" : "network");
    }
  }

  // Legacy branch: raw email + password stored by builds ≤ 1.1(31).
  let creds: { username?: string; password?: string } | null = null;
  try {
    creds = await NativeBiometric.getCredentials({ server: LEGACY_KEYCHAIN_SERVER });
  } catch {
    creds = null;
  }
  if (!creds || !creds.username || !creds.password) {
    await clearLegacy();
    throw new BiometricError("expired");
  }
  try {
    const { data } = await api.post<DeviceLoginResponse>("/auth/login", {
      email: creds.username,
      password: creds.password,
    });
    // They already use biometrics here: opt them in so the post-login re-arm
    // moves this install onto a device key (and deletes the stored password).
    if (data && data.user) setOptIn(data.user._id, true);
    return data;
  } catch (err: any) {
    const status = err && err.response && err.response.status;
    if (status === 400 || status === 401) {
      await clearLegacy();
      throw new BiometricError("expired");
    }
    throw new BiometricError(err && err.response ? "failed" : "network");
  }
}
